import { useEffect } from 'react'
import { useRouteError } from 'react-router-dom'
import { ErrorState } from '@/components/ui/ErrorState'

/** A failed dynamic import — the usual cause is a new deploy invalidating the
 *  chunk URLs an already-open tab still references. */
function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : typeof error === 'string' ? error : ''
  return /dynamically imported module|module script failed|importing a module/i.test(msg)
}

const RELOAD_KEY = 'sv-chunk-reload-at'

/**
 * Route-level error boundary (wired as `errorElement`). Renders the token-themed
 * `ErrorState` instead of React Router's bare default, so an unexpected render
 * error re-skins with the pass-2 palette. If the cause is a stale code-split
 * chunk (a deploy invalidated the URLs an open tab references), we reload once
 * to pull the fresh chunks; the 10s throttle prevents a reload loop.
 */
export function RouteError() {
  const error = useRouteError()
  const chunkError = isChunkLoadError(error)

  useEffect(() => {
    if (!chunkError) return
    const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? '0')
    if (Date.now() - last > 10_000) {
      sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
      window.location.reload()
    }
  }, [chunkError])

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <ErrorState
        title={chunkError ? 'Updating…' : 'Something went wrong'}
        description={
          chunkError
            ? 'A newer version is available. Reloading to get the latest…'
            : 'This page didn’t load. Reloading usually fixes it.'
        }
        onRetry={() => window.location.reload()}
      />
    </div>
  )
}
